import { Injectable, Injector, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastNotificationService } from '@core/services/toast-notification.service';
import { CompanyService } from '@core/services/company.service';
import { PARTNER_PLAN_MESSAGE, isChannelPartnerCompany } from '@core/utils/channel-partner';

/**
 * CreditDepletionInterceptor
 *
 * Intercepts HTTP 402 responses from the API when the error code is
 * `INSUFFICIENT_CREDITS`. Surfaces an in-app error toast with an upgrade
 * CTA that deep-links to the Coins tab on the Company settings page.
 */
@Injectable()
export class CreditDepletionInterceptor implements HttpInterceptor {
  private router = inject(Router);
  private toastService = inject(ToastNotificationService);
  // Resolved lazily: CompanyService depends on HttpClient, which depends on
  // the interceptors.
  private injector = inject(Injector);

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: unknown) => {
        if (
          err instanceof HttpErrorResponse &&
          err.status === 402 &&
          (err.error as any)?.code === 'INSUFFICIENT_CREDITS' &&
          isChannelPartnerCompany(this.injector.get(CompanyService).company)
        ) {
          // A partner company buys nothing here (#943): no "Buy Credits".
          this.toastService.showError('Insufficient Coins', `You have run out of Coins for this action. ${PARTNER_PLAN_MESSAGE}`, 8000);
        } else if (
          err instanceof HttpErrorResponse &&
          err.status === 402 &&
          (err.error as any)?.code === 'INSUFFICIENT_CREDITS'
        ) {
          this.toastService.showError(
            'Insufficient Coins',
            'You have run out of credits for this action. Top up to continue.',
            8000,
            {
              label: 'Buy Credits',
              callback: () =>
                this.router.navigate(['/dashboard/company'], {
                  queryParams: { tab: 'sni-tokens' },
                }),
            }
          );
        }
        return throwError(() => err);
      })
    );
  }
}
